/**
 * Fallakte (Case): zentrale Entität pro Tragfähigkeitsprüfung.
 *
 * Diskriminator `type` trennt B2C (Mandant beauftragt) und B2G (Jobcenter
 * beauftragt). Bei B2G ist das Jobcenter-Aktenzeichen + Bewilligungsbescheid
 * Pflicht (Audit-Trail Anforderung), bei B2C optional.
 *
 * `legalBasis` mappt auf den jeweiligen Förderkontext nach SGB-II/III:
 *   - par10:  § 10 Abs. 2 Nr. 5 SGB II (Bestandsprüfung, ab 1. Juli 2026)
 *   - par16b: § 16b SGB II (Einstiegsgeld)
 *   - par16c: § 16c SGB II (Förderung Selbständiger)
 *   - par93:  § 93 SGB III (Gründungszuschuss; Vorab-Tragfähigkeit)
 */
import { z } from 'zod';
import { CaseIdSchema, JobcenterIdSchema, UserIdSchema } from './ids.js';

export const CaseTypeSchema = z.enum(['b2c', 'b2g']);
export type CaseType = z.infer<typeof CaseTypeSchema>;

export const LegalBasisSchema = z.enum(['par10', 'par16b', 'par16c', 'par93']);
export type LegalBasis = z.infer<typeof LegalBasisSchema>;

export const CaseStatusSchema = z.enum([
  'draft',
  'data_collection',
  'analysis',
  'review',
  'statement_pending',
  'completed',
  'archived',
]);
export type CaseStatus = z.infer<typeof CaseStatusSchema>;

const CaseBaseSchema = z.object({
  id: CaseIdSchema,
  userId: UserIdSchema,
  legalBasis: LegalBasisSchema,
  status: CaseStatusSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
});

const B2cCaseSchema = CaseBaseSchema.extend({
  type: z.literal('b2c'),
  jobcenterId: JobcenterIdSchema.optional(),
  jobcenterFileNumber: z.string().min(1).max(64).optional(),
});

const B2gCaseSchema = CaseBaseSchema.extend({
  type: z.literal('b2g'),
  // Bei B2G ist die Jobcenter-Zuordnung Pflicht — sonst kein gültiger Auftrag.
  jobcenterId: JobcenterIdSchema,
  jobcenterFileNumber: z.string().min(1).max(64),
  contractReference: z.string().min(1).max(128).optional(),
});

export const CaseSchema = z.discriminatedUnion('type', [B2cCaseSchema, B2gCaseSchema]);

export type B2cCase = z.infer<typeof B2cCaseSchema>;
export type B2gCase = z.infer<typeof B2gCaseSchema>;
export type Case = z.infer<typeof CaseSchema>;
